import type {
  FactsetChartFormat,
  FactsetChartParameters,
  FactsetChartSummaryTable,
} from "./types";
import {
  CHART_CAPABILITY_ERROR_PREFIX,
  findChartCapability,
  isKnownUnsupportedChartType,
  suggestChartCapability,
  type ChartCapability,
  type ChartSystem,
} from "./chartCapabilityRegistry";

export type ChartCreatorGeneratePreview = "None" | "Png" | string;

export interface ChartCreatorPayloadOptions {
  isEdit: boolean;
  generatePreview: ChartCreatorGeneratePreview;
}

export interface PreviewChartPayloadOptions {
  isEdit?: boolean;
  generatePreview?: ChartCreatorGeneratePreview;
}

export interface ChartCreatorPayload {
  data: {
    chartParameters: {
      chartType: string;
      companyNamesJson: string;
      tickers: string[];
      frequency: string;
      startDate: string;
      endDate: string;
      horizon: string;
      annotations: Array<{
        content: string;
        date: string;
        dateTime: string;
        entity: string | undefined;
        isPeerGroup: boolean;
      }>;
      stylesetId: string;
      mathFunction: string;
      quickLabel: FactsetChartParameters["quickLabel"];
      currencies: FactsetChartParameters["currencies"];
      estimateParameters: NonNullable<FactsetChartParameters["estimateParameters"]>;
    };
    chartFormat: FactsetChartFormat;
    isEdit: boolean;
    generatePreview: ChartCreatorGeneratePreview;
  };
}

export interface DownloadExcelChartPayload {
  data: Pick<ChartCreatorPayload["data"], "chartParameters" | "chartFormat"> & {
    chartParameters: ChartCreatorPayload["data"]["chartParameters"] & {
      summaryTable?: FactsetChartSummaryTable;
    };
  };
}

/**
 * Whether `/download-excel-chart` is sent a `summaryTable` override at all.
 *
 * Section 19 of `f2_docs/charting/chart-creator-api-parameter-skill.md` defines
 * the `Display` / `Period1`-`Period5` vocabulary a request may carry, and the
 * table is produced when the workbook is generated ("Chart Creator is
 * responsible for initial creation only"). Whether the endpoint acts on the
 * field is a separate question: `chart-creator-api-reference.md` calls it "not
 * fully implemented yet", and a workbook downloaded without it has arrived with
 * no table at all. So the field is sent when a caller asks for a table, and
 * callers report it as requested rather than as applied.
 *
 * A standard request omits `summaryTable` — the template's own default covers
 * it — so only an explicit user override is ever serialized.
 */
export const CHART_CREATOR_SUMMARY_TABLE_API = true;

export const DEFAULT_CHART_FORMAT: FactsetChartFormat = {
  fontHeight: 12,
  heading1FontHeight: 11,
  xGrid: true,
  yGrid: true,
  yAxisPosition: "Right",
  xLabelFormat: "mm/dd/yy",
};

export const DEFAULT_ESTIMATE_PARAMS: NonNullable<FactsetChartParameters["estimateParameters"]> = {
  period: "A",
  calendarized: "0",
  estimatePeriods: "3",
  actualHistory: "5",
  statistic: "mean",
  showE: "1",
  showA: "1",
} as const;

function nonEmpty(value: string | undefined, fallback: string): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : fallback;
}

export function timeframeDefaultsForChartType(
  chartType: string,
): { startDate: string; frequency: string } {
  if (/^(FIN_|EST_)/i.test(chartType)) {
    return { startDate: "-5AY", frequency: "Q" };
  }
  if (/^(VAL_|SIZE_|FIX_)/i.test(chartType)) {
    return { startDate: "-3AY", frequency: "M" };
  }
  return { startDate: "-1AY", frequency: "D" };
}

export function normalizeActiveGraphChartParameters(
  params: FactsetChartParameters,
): FactsetChartParameters {
  const chartType = typeof params.chartType === "string" ? params.chartType.trim() : "";
  const timeframe = timeframeDefaultsForChartType(chartType);
  return {
    ...params,
    chartType,
    frequency: nonEmpty(params.frequency, timeframe.frequency),
    startDate: nonEmpty(params.startDate, timeframe.startDate),
    endDate: nonEmpty(params.endDate, "NOW"),
  };
}

function annotationDateTime(date: string, dateTime: string | undefined): string {
  const explicit = typeof dateTime === "string" ? dateTime.trim() : "";
  if (explicit.length > 0) return explicit;
  const trimmedDate = typeof date === "string" ? date.trim() : "";
  if (trimmedDate.length === 0) return "";
  const parsedDate = new Date(trimmedDate);
  return Number.isNaN(parsedDate.getTime()) ? trimmedDate : parsedDate.toISOString();
}

function fallbackSuggestion(system: ChartSystem): readonly ChartCapability[] {
  const id = system === "factset_activegraph" ? "P_PRICE" : "xlColumnClustered";
  const capability = findChartCapability(id);
  return capability ? [capability] : [];
}

function buildUnknownChartTypeMessage(
  chartType: string,
  expectedSystem: ChartSystem,
  callerLabel: string,
  wrongSystemCapability: ReturnType<typeof findChartCapability>,
): string {
  const systemLabel =
    expectedSystem === "factset_activegraph"
      ? "FactSet ActiveGraph (PreviewChart / AddActiveGraph)"
      : "native Excel chart (AddExcelChart)";

  if (wrongSystemCapability !== null) {
    const otherLabel =
      wrongSystemCapability.system === "factset_activegraph"
        ? "FactSet ActiveGraph"
        : "native Excel chart";
    return (
      `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: \`chartType\` "${chartType}" is a ${otherLabel} type ` +
      `but this call expects a ${systemLabel} type. Route the request through the ${otherLabel} ` +
      `code path instead, or pick a ${systemLabel} type.`
    );
  }

  let suggestions = suggestChartCapability(chartType, 3, { system: expectedSystem });
  if (suggestions.length === 0 && isKnownUnsupportedChartType(chartType)) {
    suggestions = fallbackSuggestion(expectedSystem);
  }

  const suggestionText =
    suggestions.length > 0
      ? ` Closest available: ${suggestions
          .map((capability) => `\`${capability.id}\` (${capability.displayName})`)
          .join(", ")}.`
      : "";

  if (isKnownUnsupportedChartType(chartType)) {
    return (
      `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: chart type "${chartType}" is not supported today.` +
      suggestionText
    );
  }

  return (
    `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: \`chartType\` "${chartType}" is not in the ${systemLabel} catalog ` +
    `(see chartCapabilityRegistry.ts for the full list of supported codes).` +
    suggestionText
  );
}

export function assertChartTypeInRegistry(
  chartType: string,
  expectedSystem: ChartSystem,
  callerLabel: string,
): string {
  const trimmed = typeof chartType === "string" ? chartType.trim() : "";
  const capability = findChartCapability(trimmed);
  if (capability !== null && capability.system === expectedSystem) {
    return trimmed;
  }
  throw new Error(buildUnknownChartTypeMessage(trimmed, expectedSystem, callerLabel, capability));
}

export function assertChartParametersValid(
  params: FactsetChartParameters,
  callerLabel: string,
): void {
  if (!Array.isArray(params.tickers) || params.tickers.length === 0) {
    throw new Error(`${callerLabel}: \`tickers\` is required (e.g. ["AAPL-US"])`);
  }
  const trimmedChartType =
    typeof params.chartType === "string" ? params.chartType.trim() : "";
  if (trimmedChartType.length === 0) {
    throw new Error(`${callerLabel}: \`chartType\` is required (e.g. "VAL_PE2")`);
  }
  assertChartTypeInRegistry(trimmedChartType, "factset_activegraph", callerLabel);
}

export function buildChartCreatorPayload(
  params: FactsetChartParameters,
  options: ChartCreatorPayloadOptions,
): ChartCreatorPayload {
  const normalizedParams = normalizeActiveGraphChartParameters(params);
  const companyNames = normalizedParams.companyNames ?? { single: [], aggregated: [] };
  const companyNamesJson = JSON.stringify({
    SingleCompanies: companyNames.single ?? [],
    AggregatedCompanies: companyNames.aggregated ?? [],
  });

  const annotations = (normalizedParams.annotations ?? []).map((annotation) => ({
    content: annotation.content,
    date: annotation.date,
    dateTime: annotationDateTime(annotation.date, annotation.dateTime),
    entity: annotation.entity ?? normalizedParams.tickers[0],
    isPeerGroup: annotation.isPeerGroup ?? false,
  }));

  return {
    data: {
      chartParameters: {
        chartType: normalizedParams.chartType ?? "",
        companyNamesJson,
        tickers: normalizedParams.tickers,
        frequency: normalizedParams.frequency ?? "D",
        startDate: normalizedParams.startDate ?? "-1AY",
        endDate: normalizedParams.endDate ?? "NOW",
        horizon: normalizedParams.horizon ?? "",
        annotations,
        stylesetId: normalizedParams.stylesetId ?? "",
        mathFunction: normalizedParams.mathFunction ?? "",
        quickLabel: normalizedParams.quickLabel ?? 0,
        currencies: normalizedParams.currencies ?? [],
        estimateParameters: normalizedParams.estimateParameters ?? DEFAULT_ESTIMATE_PARAMS,
      },
      chartFormat: { ...DEFAULT_CHART_FORMAT, ...(normalizedParams.chartFormat ?? {}) },
      isEdit: options.isEdit,
      generatePreview: options.generatePreview,
    },
  };
}

export function buildPreviewChartPayload(
  params: FactsetChartParameters,
  options: PreviewChartPayloadOptions = {},
): ChartCreatorPayload {
  assertChartParametersValid(params, "previewChart");
  return buildChartCreatorPayload(params, {
    isEdit: options.isEdit ?? false,
    generatePreview: options.generatePreview ?? "Png",
  });
}

/**
 * Builds the direct `/download-excel-chart` request. The download endpoint
 * accepts the shared parameters/format contract but not PreviewChart's
 * `isEdit` or `generatePreview` controls.
 *
 * `summaryTable` is only serialized when `CHART_CREATOR_SUMMARY_TABLE_API` is
 * on. With the flag off the field is dropped here rather than at the call
 * site, so there is exactly one place that decides whether the upstream is
 * trusted with it.
 */
export function buildDownloadExcelChartPayload(
  params: FactsetChartParameters,
  options: { summaryTableApiEnabled?: boolean } = {},
): DownloadExcelChartPayload {
  assertChartParametersValid(params, "downloadExcelChart");
  const shared = buildChartCreatorPayload(params, {
    isEdit: false,
    generatePreview: "None",
  });
  const summaryTableEnabled =
    options.summaryTableApiEnabled ?? CHART_CREATOR_SUMMARY_TABLE_API;
  const summaryTable =
    summaryTableEnabled && params.summaryTable ? params.summaryTable : undefined;
  return {
    data: {
      chartParameters: {
        ...shared.data.chartParameters,
        ...(summaryTable ? { summaryTable } : {}),
      },
      chartFormat: shared.data.chartFormat,
    },
  };
}
